/**
 * ☰ › AI usage — what the reader's own AI use cost: Summary, Describe, chat.
 *
 * `docs/features/summary.md` § "Every charge is a ledger row, per user". The
 * member's view of the ledger ☰ Admin › Ledger shows in full: one person's
 * charges, the period's total, and how much of today's budget is gone. No model
 * and no list price — the server never sends them (owner, 2026-10-18).
 *
 * Times are Pacific, as everything the owner reads is; the budget resets at UTC
 * midnight, so the reset is shown converted rather than as "midnight".
 */

import { useCallback, useEffect, useState } from "react";
import { plainText } from "@qufolio/screener-grid";

import { fetchMyUsage, type MyUsage } from "../api/usage";
import { usd } from "./AdminLedger";

/** Days; `null` is every row. 7 by default, as the ledger is. */
const PERIODS: { days: number | null; label: string }[] = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "365 days" },
  { days: null, label: "All" },
];

const PART: Record<string, string> = {
  summary: "note",
  "summary-personal": "personal",
  describe: "describe",
  chat: "chat",
  "macro-note": "macro note",
};

/** A chat turn's ref is up to 60 characters of the message — cut, with the whole on hover. */
const FOR: React.CSSProperties = {
  maxWidth: 220,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const BODY: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: "10px 12px 12px",
  minWidth: 380,
  maxWidth: "min(560px, 92vw)",
  overflowY: "auto",
  color: "var(--qf-text)",
  fontSize: 12,
};
const BAR: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8 };
const NOTE: React.CSSProperties = { color: "var(--qf-text-secondary)", fontSize: 12 };
const MUTED: React.CSSProperties = { color: "var(--qf-text-muted)", fontSize: 12 };
const ERROR: React.CSSProperties = { ...NOTE, color: "var(--qf-negative, #d66)" };
const SCROLL: React.CSSProperties = { overflowX: "auto", maxWidth: "100%" };
const TABLE: React.CSSProperties = { borderCollapse: "collapse", width: "100%", fontSize: 12 };
const TH: React.CSSProperties = {
  textAlign: "left",
  fontWeight: 500,
  color: "var(--qf-text-muted)",
  padding: "4px 8px",
  borderBottom: "1px solid var(--qf-border)",
  whiteSpace: "nowrap",
};
const TD: React.CSSProperties = { padding: "4px 8px", whiteSpace: "nowrap" };
const NUM: React.CSSProperties = { ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" };
const INPUT: React.CSSProperties = {
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "3px 8px",
  fontSize: 12,
};

const PT = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

function ptParts(iso: string): Record<string, string> | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return Object.fromEntries(PT.formatToParts(at).map((p) => [p.type, p.value]));
}

/** `MM-DD HH:mm` in Pacific time. */
export function ptWhen(iso: string): string {
  const p = ptParts(iso);
  return p ? `${p.month}-${p.day} ${p.hour}:${p.minute}` : iso;
}

/** `HH:mm` in Pacific time. */
export function ptClock(iso: string): string {
  const p = ptParts(iso);
  return p ? `${p.hour}:${p.minute}` : iso;
}

function notes(n: number): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? "note" : "notes"}`;
}

function tokens(input: number, output: number): string {
  return `${input.toLocaleString("en-US")} / ${output.toLocaleString("en-US")}`;
}

export function todayLine(today: NonNullable<MyUsage["today"]>): string {
  if (today.limit === null) return `Today: ${notes(today.used)}`;
  return `Today: ${today.used} of ${notes(today.limit)} · resets ${ptClock(today.resetsAt)} PT`;
}

export function AiUsage() {
  const [days, setDays] = useState<number | null>(7);
  const [usage, setUsage] = useState<MyUsage | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    let alive = true;
    setError(null);
    setUsage(null);
    fetchMyUsage(days)
      .then((found) => alive && setUsage(found))
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [days]);
  useEffect(reload, [reload]);

  return (
    <div style={BODY}>
      <div style={BAR}>
        <span style={NOTE}>Period</span>
        <select
          style={INPUT}
          value={days ?? "all"}
          onChange={(e) => setDays(e.target.value === "all" ? null : Number(e.target.value))}
          aria-label="Period"
        >
          {PERIODS.map((p) => (
            <option key={p.label} value={p.days ?? "all"}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      {error && <div style={ERROR}>{plainText(error)}</div>}
      {usage === null && !error && <div style={MUTED}>Loading…</div>}

      {usage && (
        <>
          <div>
            {/* Charges, not notes: a note and its personal part are two rows
                here and one Summary on the Today line, and a Describe is no
                note at all. */}
            {usage.totals.charges.toLocaleString("en-US")}{" "}
            {usage.totals.charges === 1 ? "charge" : "charges"} · {usd(usage.totals.billedUsd)} ·{" "}
            {tokens(usage.totals.inputTokens, usage.totals.outputTokens)} tokens
          </div>
          {usage.today && <div style={NOTE}>{todayLine(usage.today)}</div>}

          {usage.charges.length === 0 ? (
            <div style={NOTE}>No AI use in this period.</div>
          ) : (
            <div style={SCROLL}>
              <table style={TABLE}>
                <thead>
                  <tr>
                    {["Time (PT)", "Part", "For"].map((h) => (
                      <th key={h} style={TH}>
                        {h}
                      </th>
                    ))}
                    {["Cost", "In / out tokens"].map((h) => (
                      <th key={h} style={{ ...TH, textAlign: "right" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {usage.charges.map((r, i) => (
                    <tr key={`${r.at}-${i}`}>
                      <td style={TD}>{ptWhen(r.at)}</td>
                      <td style={TD}>{PART[r.reason] ?? r.reason}</td>
                      {/* A symbol for a Summary, the message's start for a chat turn. */}
                      <td style={{ ...TD, ...FOR }} title={r.ref ?? undefined}>
                        {r.ref ?? "—"}
                      </td>
                      <td style={NUM}>{usd(r.billedUsd)}</td>
                      <td style={NUM}>{tokens(r.inputTokens, r.outputTokens)}</td>
                    </tr>
                  ))}
                </tbody>
                {usage.charges.length === 200 && (
                  <caption style={{ ...MUTED, captionSide: "bottom", textAlign: "left" }}>
                    The newest 200 — narrow the period for older ones.
                  </caption>
                )}
              </table>
            </div>
          )}
        </>
      )}

      <div style={MUTED}>Opening a note someone already wrote is free and doesn't appear here.</div>
    </div>
  );
}
